import { useState } from 'react';

import { ErrorBox, Spinner, formatDateTime, useAsync } from '@solar/ui';

import { api } from '../api';

/**
 * 2018년도 전자계약서 조회 — 레거시 order/pdf_2018 (OrderController@pdf_inquiry) 파리티.
 *
 * 생성기가 아니다. 2018년에 미리 만들어 둔 계약서 PDF 를 자치구별로 나열하고
 * 내려받게만 한다. 레거시는 디렉터리 목록·파일 목록을 서버 디스크에서 읽고
 * 다운로드 링크를 공개 URL 로 하드코딩했다; 신규는 S3 목록 + presigned URL 이라
 * 링크가 곧 만료된다 — 페이지를 다시 읽으면 새 링크가 온다.
 * 레거시에는 페이징이 없었다(구 하나를 통째로). 여기서는 20건씩 넘긴다.
 * 접근 역할(4,8 제외)은 서버가 강제한다.
 */

const SIZE = 20;

/** 바이트를 사람이 읽는 크기로 — 계약서 PDF 는 대체로 수백 KB 라 KB/MB 면 충분하다. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function Pdf2018() {
  // '' = 아직 선택 전 → 서버가 목록 첫 번째 자치구를 쓴다. 자치구 목록이 오면
  // 셀렉트도 같은 값을 보여주도록 맞춘다 (레거시 기본값 '강남구' 하드코딩 대체).
  const [gu, setGu] = useState('');
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);

  const districts = useAsync(() => api.listPdf2018Districts(), []);
  const list = useAsync(
    () => api.listPdf2018({ gu: gu || undefined, q: applied, page, size: SIZE }),
    [gu, applied, page],
  );

  const options = districts.data?.districts ?? [];
  const selected = gu || options[0] || '';
  const total = list.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / SIZE));

  return (
    <div className="admin__body">
      <section className="admin__list">
        <div className="toolbar">
          <select
            aria-label="자치구"
            value={selected}
            disabled={options.length === 0}
            onChange={(e) => {
              setGu(e.target.value);
              setPage(1);
            }}
          >
            {options.length === 0 && <option value="">자치구 없음</option>}
            {options.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          <form
            className="search"
            onSubmit={(e) => {
              e.preventDefault();
              setApplied(keyword.trim());
              setPage(1);
            }}
          >
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="이름을 입력해주세요."
            />
            <button type="submit">검색</button>
          </form>
        </div>

        {districts.error && <ErrorBox message={districts.error} onRetry={districts.reload} />}
        {list.loading && <Spinner />}
        {list.error && <ErrorBox message={list.error} onRetry={list.reload} />}

        {list.data && (
          <table className="grid">
            <thead>
              <tr>
                <th className="num">No</th>
                <th>파일명</th>
                <th className="num">크기</th>
                <th>수정일</th>
                <th>다운로드</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    등록된 2018년도 전자계약서가 없습니다.
                  </td>
                </tr>
              )}
              {list.data.items.map((file, i) => (
                <tr key={file.name}>
                  <td className="num">{(page - 1) * SIZE + i + 1}</td>
                  <td>{file.name}</td>
                  <td className="num">{formatSize(file.size)}</td>
                  <td>{formatDateTime(file.last_modified)}</td>
                  <td>
                    {/* 레거시 '계약서 다운로드' 버튼(target=_blank) 그대로. */}
                    <a href={file.download_url} target="_blank" rel="noreferrer">
                      계약서 다운로드
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="pager">
          <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
            이전
          </button>
          <span>
            {page} / {lastPage} (총 {total}건)
          </span>
          <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= lastPage}>
            다음
          </button>
        </div>
      </section>
    </div>
  );
}
